import React, { useState, useEffect } from "react";
import Select from "react-select";
import "../assets/OnboardDeviceFleet.css";
import "../assets/OnboardLogMask.css";
import uploadIcon from '../assets/upload.svg';
import selectedFileIcon from '../assets/selected_file.svg';
import { fetchCities, fetchDeviceMakes, fetchDeviceModels, fetchDeviceOperators, fetchOnboardedDevices, onboardDevice, deboardDevice, bulkOnboardDevices } from '../api/restapi';

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: '38px',
    height: '38px',
    borderColor: 'rgb(103, 114, 132)',
  }),
  valueContainer: (base) => ({
    ...base,
    height: '38px',
    padding: '0 8px',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: '38px',
  }),
  placeholder: (base) => ({
    ...base,
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    margin: 0,
  }),
  singleValue: (base) => ({
    ...base,
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    margin: 0,
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '14px',
    color: state.isSelected ? 'white' : 'rgba(0, 0, 0, 0.85)',
    padding: '8px 12px',
    backgroundColor: state.isSelected
      ? 'rgb(50, 83, 220)'
      : state.isFocused
      ? '#f0f4ff'
      : 'white',
  }),
};

function OnboardDeviceFleet({ userInfo }) {
  // Two-step flow state
  const [currentStep, setCurrentStep] = useState(1);
  const [validationError, setValidationError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Device form state
  const [deviceForm, setDeviceForm] = useState({
    deviceId: "",
    make: "",
    model: "",
    operator: "",
    city: "",
    lat: "",
    lng: "",
    thrMax: "",
    thrMin: ""
  });

  // API state for dropdowns and onboarded devices
  const [apiCities, setApiCities] = useState([]);
  const [apiMakes, setApiMakes] = useState([]);
  const [apiModels, setApiModels] = useState([]);
  const [apiOperators, setApiOperators] = useState([]);
  const [apiOnboardedDevices, setApiOnboardedDevices] = useState([]);
  const [apiLoading, setApiLoading] = useState({
    cities: false,
    onboardedDevices: false
  });
  const [apiError, setApiError] = useState({
    cities: null,
    onboardedDevices: null
  });

  // Onboarded devices state (for localStorage fallback)
  const [onboardedDevices, setOnboardedDevices] = useState([]);
  const [cityFilter, setCityFilter] = useState("All");
  const [deboardingId, setDeboardingId] = useState(null);

  // CSV import state
  const [csvImportState, setCsvImportState] = useState({
    file: null,
    validDevices: [],
    errors: [],
    importing: false,
    stats: { processed: 0, valid: 0, invalid: 0 }
  });

  // Load makes, models, operators from API on mount
  useEffect(() => {
    const loadMakes = async () => {
      try {
        const response = await fetchDeviceMakes();
        if (response.success && response.data && response.data.makes) {
          setApiMakes(response.data.makes);
        }
      } catch (error) {
        console.error("Error loading device makes:", error);
      }
    };
    const loadModels = async () => {
      try {
        const response = await fetchDeviceModels();
        if (response.success && response.data && response.data.models) {
          setApiModels(response.data.models);
        }
      } catch (error) {
        console.error("Error loading device models:", error);
      }
    };
    const loadOperators = async () => {
      try {
        const response = await fetchDeviceOperators();
        if (response.success && response.data && response.data.operators) {
          setApiOperators(response.data.operators);
        }
      } catch (error) {
        console.error("Error loading device operators:", error);
      }
    };
    loadMakes();
    loadModels();
    loadOperators();
  }, []);

  // Load cities from API on mount
  useEffect(() => {
    const loadCities = async () => {
      setApiLoading(prev => ({ ...prev, cities: true }));
      setApiError(prev => ({ ...prev, cities: null }));
      
      try {
        const response = await fetchCities();
        if (response.success && response.data && response.data.cities) {
          setApiCities(response.data.cities);
        } else {
          throw new Error('Invalid cities response format');
        }
      } catch (error) {
        console.error("Error loading cities:", error);
        setApiError(prev => ({ ...prev, cities: error.message }));
        setApiCities([
          { city_id: "city_sd", city_name: "San Diego", state: "California" },
          { city_id: "city_irv", city_name: "Irvine", state: "California" },
          { city_id: "city_sf", city_name: "San Francisco", state: "California" }
        ]);
      } finally {
        setApiLoading(prev => ({ ...prev, cities: false }));
      }
    };

    loadCities();
  }, []);

  // Load onboarded devices from API on mount
  useEffect(() => {
    const loadOnboardedDevices = async () => {
      setApiLoading(prev => ({ ...prev, onboardedDevices: true }));
      setApiError(prev => ({ ...prev, onboardedDevices: null }));
      
      try {
        const response = await fetchOnboardedDevices();
        if (response.success && response.data && response.data.devices) {
          setApiOnboardedDevices(response.data.devices);
        } else {
          throw new Error('Invalid onboarded devices response format');
        }
      } catch (error) {
        console.error("Error loading onboarded devices:", error);
        setApiError(prev => ({ ...prev, onboardedDevices: error.message }));
        const stored = localStorage.getItem("qfleet_onboarded");
        if (stored) {
          try {
            const devices = JSON.parse(stored);
            setOnboardedDevices(devices);
          } catch (parseError) {
            console.error("Error parsing localStorage devices:", parseError);
          }
        }
      } finally {
        setApiLoading(prev => ({ ...prev, onboardedDevices: false }));
      }
    };

    loadOnboardedDevices();
  }, []);

  // Seeded RNG for deterministic device generation
  function mulberry32(a) {
    return function() {
      var t = a += 0x6D2B79F5;
      t = Math.imul(t ^ t >>> 15, t | 1);
      t ^= t + Math.imul(t ^ t >>> 7, t | 61);
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    }
  }
  const randFor = (seed) => mulberry32(seed.split("").reduce((a, c) => a + c.charCodeAt(0), 0));

  // City mapping for GPS coordinates
  const cityMapping = {
    "San Diego": {
      id: "city_sd",
      bounds: { latMin: 32.6, latMax: 33.2, lngMin: -117.3, lngMax: -116.8 }
    },
    "Irvine": {
      id: "city_irv", 
      bounds: { latMin: 33.6, latMax: 33.8, lngMin: -117.9, lngMax: -117.7 }
    },
    "San Francisco": {
      id: "city_sf",
      bounds: { latMin: 37.68, latMax: 37.83, lngMin: -122.52, lngMax: -122.36 }
    }
  };

  // Form validation
  const validateForm = () => {
    const { deviceId, make, model, operator, city, lat, lng, thrMax, thrMin } = deviceForm;
    
    if (!deviceId.trim() || !make.trim() || !model.trim() || !operator.trim() || !city.trim() || !thrMax.trim() || !thrMin.trim()) {
      return "All fields are required.";
    }

    if (!lat.toString().trim() || !lng.toString().trim()) {
      return "Latitude and Longitude are required.";
    }

    const latitude = Number(lat);
    const longitude = Number(lng);
    if (isNaN(latitude) || isNaN(longitude)) {
      return "Latitude and Longitude must be numeric.";
    }

    if (latitude < -90 || latitude > 90) {
      return "Latitude must be between -90 and 90.";
    }
    if (longitude < -180 || longitude > 180) {
      return "Longitude must be between -180 and 180.";
    }

    const maxThr = Number(thrMax);
    const minThr = Number(thrMin);
    if (isNaN(maxThr) || isNaN(minThr)) {
      return "Throughput values must be numeric.";
    }
    if (maxThr < minThr) {
      return "Max Throughput must be greater than or equal to Min Throughput.";
    }

    const existingIds = onboardedDevices.map(d => d.id);
    if (existingIds.includes(deviceId.trim())) {
      return "Device ID already exists in fleet.";
    }

    return null;
  };

  const handleInputChange = (field, value) => {
    setDeviceForm(prev => ({ ...prev, [field]: value }));
  };

  const handleContinue = () => {
    const error = validateForm();
    if (error) {
      setValidationError(error);
      return;
    }
    setValidationError("");
    setCurrentStep(2);
  };

  const handleGoBack = () => {
    setCurrentStep(1);
    setSuccessMessage("");
    setErrorMessage("");
  };

  const getDeviceStatus = (minThroughput) => {
    if (minThroughput < 20) return "poor";
    if (minThroughput < 30) return "warn";
    return "good";
  };

  const generateCoordinates = (cityName) => {
    const cityData = cityMapping[cityName];
    if (!cityData) return { lat: 0, lng: 0 };
    const rng = randFor(deviceForm.deviceId);
    const { latMin, latMax, lngMin, lngMax } = cityData.bounds;
    return {
      lat: latMin + (latMax - latMin) * rng(),
      lng: lngMin + (lngMax - lngMin) * rng()
    };
  };

  const handleSubmit = async () => {
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const { deviceId, make, model, operator, city, lat, lng, thrMax, thrMin } = deviceForm;
      
      const selectedCity = apiCities.find(c => c.city_name === city);
      const cityId = selectedCity ? selectedCity.city_id : city;
      
      const deviceData = {
        system_id: deviceId.trim(),
        make: make.trim(),
        model: model.trim(),
        operator: operator.trim(),
        city_id: cityId,
        latitude: parseFloat(lat),
        longitude: parseFloat(lng),
        throughput_max: parseInt(thrMax),
        throughput_min: parseInt(thrMin),
        onboarded_user: userInfo || 'unknown'
      };

      const response = await onboardDevice(deviceData);
      
      if (response.success) {
        setSuccessMessage("Device onboarded successfully!");
        const devicesResponse = await fetchOnboardedDevices();
        if (devicesResponse.success && devicesResponse.data && devicesResponse.data.devices) {
          setApiOnboardedDevices(devicesResponse.data.devices);
        }
        window.dispatchEvent(new CustomEvent('deviceOnboarded', { detail: response.data }));
      } else {
        setErrorMessage(response.error || "Failed to onboard device.");
      }
      
    } catch (error) {
      console.error("Error onboarding device:", error);
      setErrorMessage(error.errorMessage || "Failed to onboard device.");
    }
  };

  const handleStartNew = () => {
    setDeviceForm({ deviceId: "", make: "", model: "", operator: "", city: "", lat: "", lng: "", thrMax: "", thrMin: "" });
    setCurrentStep(1);
    setValidationError("");
    setSuccessMessage("");
    setErrorMessage("");
  };

  const handleDeboardDevice = async (systemId) => {
    setDeboardingId(systemId);
    try {
      const response = await deboardDevice(systemId, userInfo || 'unknown');
      if (response.success) {
        // Refresh the onboarded devices list from API
        const devicesResponse = await fetchOnboardedDevices();
        if (devicesResponse.success && devicesResponse.data && devicesResponse.data.devices) {
          setApiOnboardedDevices(devicesResponse.data.devices);
        }
        window.dispatchEvent(new CustomEvent('deviceRemoved', { detail: systemId }));
      } else {
        console.error("Error deboarding device:", response.error || response.errorMessage);
      }
    } catch (error) {
      console.error("Error deboarding device:", error);
    } finally {
      setDeboardingId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return 'N/A';
    }
  };

  const devicesToShow = apiOnboardedDevices.length > 0 ? apiOnboardedDevices : onboardedDevices;
  const filteredDevices = cityFilter === "All" 
    ? devicesToShow 
    : devicesToShow.filter(d => {
        const cityName = d.city_name || d.cityName;
        return cityName === cityFilter;
      });

  const CSV_TEMPLATE = `system_id,make,model,city,lat,lng,thr_max,thr_min
DEV-1001,Qualcomm,X60,San Diego,32.896,-117.207,150,40
DEV-1002,Qualcomm,X62,Irvine,33.65,-117.75,120,25
DEV-1003,OEMZ,M2,San Francisco,37.776,-122.416,90,18`;

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "device_onboarding_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const parseCSV = (text) => {
    text = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const rows = [];
    let i = 0, cur = '', inQuotes = false, row = [];
    while (i < text.length) {
      const ch = text[i];
      if (inQuotes) {
        if (ch === '"') {
          if (text[i+1] === '"') { cur += '"'; i += 2; continue; }
          inQuotes = false; i++; continue;
        } else { cur += ch; i++; continue; }
      } else {
        if (ch === '"') { inQuotes = true; i++; continue; }
        if (ch === ',') { row.push(cur); cur = ''; i++; continue; }
        if (ch === '\n') { row.push(cur); rows.push(row); row = []; cur = ''; i++; continue; }
        cur += ch; i++; continue;
      }
    }
    row.push(cur); rows.push(row);
    while (rows.length && rows[rows.length-1].every(c => c === '')) rows.pop();
    return rows;
  };

  const validateCSV = (rows, headers) => {
    const needed = ["system_id", "make", "model", "state", "city", "thr_max", "thr_min"];
    const idx = {};
    headers.forEach((h, i) => idx[h.trim().toLowerCase()] = i);
    if (idx["device_id"] && !idx["system_id"]) idx["system_id"] = idx["device_id"];
    
    const missing = needed.filter(h => !(h in idx));
    if (missing.length) {
      return { valid: [], errors: [{row: 0, device_id: "", reason: `Missing headers: ${missing.join(", ")}`}], stats: { processed: 0, valid: 0, invalid: 1 } };
    }

    const existingIds = new Set(onboardedDevices.map(d => d.id));
    const fileIds = new Set();
    const valid = [];
    const errors = [];

    rows.forEach((cells, i) => {
      const rowNum = i + 2;
      const get = (name) => (cells[idx[name]] ?? "").toString().trim();
      const rowData = {
        system_id: get("system_id"), make: get("make"), model: get("model"),
        state: get("state"), city: get("city"), address: get("address") || "",
        lat: get("lat") || "", lng: get("lng") || "",
        thr_max: get("thr_max"), thr_min: get("thr_min")
      };

      const errs = [];
      needed.forEach(f => { if (!rowData[f]) errs.push(`${f} is required`); });
      if (!rowData.address && !(rowData.lat && rowData.lng)) errs.push("Provide address OR both lat and lng");
      const thrMax = Number(rowData.thr_max);
      const thrMin = Number(rowData.thr_min);
      if (isNaN(thrMax) || isNaN(thrMin)) errs.push("thr_max/thr_min must be numeric");
      if (!isNaN(thrMax) && !isNaN(thrMin) && thrMax < thrMin) errs.push("thr_max must be >= thr_min");
      if (existingIds.has(rowData.system_id)) errs.push("system_id already exists in fleet");
      if (fileIds.has(rowData.system_id)) errs.push("system_id duplicated in this file");

      if (errs.length) {
        errors.push({ row: rowNum, device_id: rowData.system_id, reason: errs.join("; ") });
      } else {
        fileIds.add(rowData.system_id);
        const coordinates = (rowData.lat && rowData.lng) 
          ? { lat: Number(rowData.lat), lng: Number(rowData.lng) }
          : generateCoordinates(rowData.city);
        const minThr = Number(rowData.thr_min);
        const maxThr = Number(rowData.thr_max);
        const avgThr = Math.round((maxThr + minThr) / 2);
        const status = getDeviceStatus(minThr);
        const rng = randFor(rowData.system_id);
        valid.push({
          id: rowData.system_id, name: rowData.system_id,
          cityId: cityMapping[rowData.city]?.id || rowData.city, cityName: rowData.city,
          lat: coordinates.lat, lng: coordinates.lng, status,
          metrics: {
            uptime: +(95 + 5 * rng()).toFixed(1), throughputMbps: avgThr,
            stallsPct: +(0.3 + 2 * rng()).toFixed(1), rsrpDbm: -88 - Math.round(15 * rng()),
            rsrqDb: -9 - Math.round(6 * rng()), snrDb: Math.round(6 + 12 * rng())
          },
          meta: {
            make: rowData.make, model: rowData.model, state: rowData.state,
            cityText: rowData.city, address: rowData.address,
            lat: rowData.lat, lng: rowData.lng, thrMax: maxThr, thrMin: minThr,
            dateAdded: new Date().toISOString()
          }
        });
      }
    });

    return { valid, errors, stats: { processed: rows.length, valid: valid.length, invalid: errors.length } };
  };

  const handleCSVUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const rows = parseCSV(text);
      if (!rows.length) {
        setCsvImportState({ file, validDevices: [], errors: [{row: 0, device_id: "", reason: "Empty file"}], importing: false, stats: { processed: 0, valid: 0, invalid: 1 } });
        return;
      }
      const result = validateCSV(rows.slice(1), rows[0]);
      setCsvImportState({ file, validDevices: result.valid, errors: result.errors, importing: false, stats: result.stats });
    } catch (error) {
      console.error("Error processing CSV:", error);
      setCsvImportState({ file, validDevices: [], errors: [{row: 0, device_id: "", reason: "Error reading file"}], importing: false, stats: { processed: 0, valid: 0, invalid: 1 } });
    }
  };

  const handleImportCSV = async () => {
    if (!csvImportState.validDevices.length) return;
    setCsvImportState(prev => ({ ...prev, importing: true }));
    try {
      const devicesForAPI = csvImportState.validDevices.map(device => {
        const selectedCity = apiCities.find(c => c.city_name === device.cityName);
        const cityId = selectedCity ? selectedCity.city_id : device.cityId;
        return {
          system_id: device.id, make: device.meta.make, model: device.meta.model,
          city_id: cityId, latitude: device.lat, longitude: device.lng,
          throughput_max: device.meta.thrMax, throughput_min: device.meta.thrMin
        };
      });
      const response = await bulkOnboardDevices({ onboarded_user: userInfo || 'unknown', devices: devicesForAPI });
      if (response.success && response.data) {
        const { successful, failed, total_processed } = response.data;
        let message = `Bulk import completed!\nTotal processed: ${total_processed}\nSuccessful: ${successful}\nFailed: ${failed}`;
        if (failed > 0) {
          message += `\n\nFailed devices:\n`;
          response.data.failed_devices.slice(0, 5).forEach(d => { message += `• ${d.system_id}: ${d.error}\n`; });
          if (response.data.failed_devices.length > 5) message += `... and ${response.data.failed_devices.length - 5} more`;
        }
        alert(message);
        const devicesResponse = await fetchOnboardedDevices();
        if (devicesResponse.success && devicesResponse.data && devicesResponse.data.devices) {
          setApiOnboardedDevices(devicesResponse.data.devices);
        }
        response.data.successful_devices.forEach(d => window.dispatchEvent(new CustomEvent('deviceOnboarded', { detail: d })));
      } else {
        alert(`Bulk import failed: ${response.message || 'Unknown error'}`);
      }
      setCsvImportState({ file: null, validDevices: [], errors: [], importing: false, stats: { processed: 0, valid: 0, invalid: 0 } });
      const fileInput = document.getElementById('csvFileInput');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error("Error importing devices:", error);
      alert(`Error importing devices: ${error.errorMessage || error.message || 'Unknown error'}`);
    } finally {
      setCsvImportState(prev => ({ ...prev, importing: false }));
    }
  };

  const downloadErrors = () => {
    if (!csvImportState.errors.length) return;
    const header = "line,device_id,reason";
    const lines = csvImportState.errors.map(e => `${e.row},${(e.device_id || "").replace(/,/g, " ")},${'"' + e.reason.replace(/"/g, '""') + '"'}`);
    const blob = new Blob([[header, ...lines].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "import_errors.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const handleCSVDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!file || !file.name.endsWith(".csv")) { alert("Please select a CSV file"); return; }
    handleCSVUpload({ target: { files: [file] } });
  };

  const handleClearCSV = () => {
    setCsvImportState({ file: null, validDevices: [], errors: [], importing: false, stats: { processed: 0, valid: 0, invalid: 0 } });
    const fileInput = document.getElementById('csvFileInput');
    if (fileInput) fileInput.value = '';
  };

  return (
    <div className="onboard-device-fleet-container">
      <div className="onboard-admin-grid">
        
        {/* LEFT: Individual Device Onboarding */}
        <div className="onboard-device-card">
          <div className="card-header">
            <h2 className="card-title">Add Device to Fleet</h2>
            <div className="card-subtitle">All fields required</div>
          </div>

          {validationError && (
            <div className="onboard-error-banner">
              <div className="onboard-error-message">{validationError}</div>
            </div>
          )}

          {currentStep === 1 && (
            <>
              <div className="onboard-form-grid">
                <div className="onboard-form-row">
                  <div className="onboard-form-field">
                    <label>System ID<span className="onboard-required-asterisk">*</span></label>
                    <input
                      type="text"
                      placeholder="e.g., DEV-000123"
                      value={deviceForm.deviceId}
                      onChange={(e) => handleInputChange('deviceId', e.target.value)}
                    />
                  </div>
                  <div className="onboard-form-field">
                    <label>Device Make<span className="onboard-required-asterisk">*</span></label>
                    <Select
                      styles={selectStyles}
                      options={apiMakes.map(make => ({ value: make, label: make }))}
                      value={deviceForm.make ? { value: deviceForm.make, label: deviceForm.make } : null}
                      onChange={(option) => handleInputChange('make', option?.value ?? '')}
                      placeholder="Select make..."
                      isClearable
                    />
                  </div>
                </div>

                <div className="onboard-form-row">
                  <div className="onboard-form-field">
                    <label>Device Model<span className="onboard-required-asterisk">*</span></label>
                    <Select
                      styles={selectStyles}
                      options={apiModels.map(model => ({ value: model, label: model }))}
                      value={deviceForm.model ? { value: deviceForm.model, label: deviceForm.model } : null}
                      onChange={(option) => handleInputChange('model', option?.value ?? '')}
                      placeholder="Select model..."
                      isClearable
                    />
                  </div>
                  <div className="onboard-form-field">
                    <label>Operator<span className="onboard-required-asterisk">*</span></label>
                    <Select
                      styles={selectStyles}
                      options={apiOperators.map(op => ({ value: op, label: op }))}
                      value={deviceForm.operator ? { value: deviceForm.operator, label: deviceForm.operator } : null}
                      onChange={(option) => handleInputChange('operator', option?.value ?? '')}
                      placeholder="Select operator..."
                      isClearable
                    />
                  </div>
                </div>

                <div className="onboard-form-row">
                  <div className="onboard-form-field">
                    <label>City<span className="onboard-required-asterisk">*</span></label>
                    <Select
                      styles={selectStyles}
                      options={apiCities.map(city => ({ value: city.city_name, label: city.city_name }))}
                      value={deviceForm.city ? { value: deviceForm.city, label: deviceForm.city } : null}
                      onChange={(option) => handleInputChange('city', option?.value ?? '')}
                      placeholder="Select city..."
                      isClearable
                    />
                  </div>
                  <div className="onboard-form-field">
                    {/* layout balance */}
                  </div>
                </div>

                <div className="onboard-form-row">
                  <div className="onboard-form-field">
                    <label>Latitude<span className="onboard-required-asterisk">*</span></label>
                    <input
                      type="number"
                      step="any"
                      placeholder="e.g., 32.896"
                      value={deviceForm.lat}
                      onChange={(e) => handleInputChange('lat', e.target.value)}
                    />
                  </div>
                  <div className="onboard-form-field">
                    <label>Longitude<span className="onboard-required-asterisk">*</span></label>
                    <input
                      type="number"
                      step="any"
                      placeholder="e.g., -117.207"
                      value={deviceForm.lng}
                      onChange={(e) => handleInputChange('lng', e.target.value)}
                    />
                  </div>
                </div>

                <div className="onboard-form-row">
                  <div className="onboard-form-field">
                    <label>Max Throughput (Mbps)<span className="onboard-required-asterisk">*</span></label>
                    <input
                      type="text"
                      placeholder="e.g., 150"
                      value={deviceForm.thrMax}
                      onChange={(e) => handleInputChange('thrMax', e.target.value)}
                    />
                  </div>
                  <div className="onboard-form-field">
                    <label>Min Throughput (Mbps)<span className="onboard-required-asterisk">*</span></label>
                    <input
                      type="text"
                      placeholder="e.g., 30"
                      value={deviceForm.thrMin}
                      onChange={(e) => handleInputChange('thrMin', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="onboard-form-actions">
                <button
                  className="onboard-submit-btn"
                  onClick={handleContinue}
                  disabled={!deviceForm.deviceId.trim() || !deviceForm.make || !deviceForm.model}
                >
                  Submit
                </button>
                <button className="onboard-back-btn" onClick={handleStartNew}>
                  Reset
                </button>
              </div>
            </>
          )}

          {currentStep === 2 && (
            <>
              <div className="onboard-review-section">
                <h3>Review your device:</h3>
                <div className="onboard-review-card">
                  <h4>Device Onboarding Request</h4>
                  <div className="onboard-review-item">
                    <span className="onboard-review-label">Device ID:</span>
                    <span className="onboard-review-value">{deviceForm.deviceId}</span>
                  </div>
                  <div className="onboard-review-item">
                    <span className="onboard-review-label">Make/Model:</span>
                    <span className="onboard-review-value">{deviceForm.make} {deviceForm.model}</span>
                  </div>
                  <div className="onboard-review-item">
                    <span className="onboard-review-label">Operator:</span>
                    <span className="onboard-review-value">{deviceForm.operator}</span>
                  </div>
                  <div className="onboard-review-item">
                    <span className="onboard-review-label">City:</span>
                    <span className="onboard-review-value">{deviceForm.city}</span>
                  </div>
                  <div className="onboard-review-item">
                    <span className="onboard-review-label">Coordinates:</span>
                    <span className="onboard-review-value">{deviceForm.lat}, {deviceForm.lng}</span>
                  </div>
                  <div className="onboard-review-item">
                    <span className="onboard-review-label">Throughput:</span>
                    <span className="onboard-review-value">{deviceForm.thrMin} - {deviceForm.thrMax} Mbps</span>
                  </div>
                </div>
              </div>

              {successMessage && (
                <div className="onboard-success-banner">
                  <div className="onboard-success-message">{successMessage}</div>
                  <button className="onboard-submit-btn" onClick={handleStartNew}>
                    Onboard Another Device
                  </button>
                </div>
              )}

              {errorMessage && (
                <div className="onboard-error-banner">
                  <div className="onboard-error-message">{errorMessage}</div>
                </div>
              )}

              {!successMessage && !errorMessage && (
                <div className="onboard-form-actions">
                  <button className="onboard-back-btn" onClick={handleGoBack}>
                    &lt; Go Back
                  </button>
                  <button className="onboard-submit-btn" onClick={handleSubmit}>
                    Onboard Device
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* RIGHT: Bulk CSV Import */}
        <div className="onboard-bulk-card">
          <div className="card-header">
            <h2 className="card-title">Bulk Import Devices (CSV)</h2>
            <div className="card-subtitle">Use the template or include headers: system_id, make, model, city, lat, lng, thr_max, thr_min</div>
          </div>
          
          <div className="input-container" style={{ marginBottom: "12px" }}>
            <label
              htmlFor="csvFileInput"
              className="custom-label"
              onDrop={handleCSVDrop}
              onDragOver={(e) => e.preventDefault()}
            >
              {csvImportState.file ? (
                <>
                  <img className="upload-icon" src={selectedFileIcon} alt="Selected file" />
                  {csvImportState.file.name}
                </>
              ) : (
                <>
                  <img className="upload-icon" src={uploadIcon} alt="Upload Icon" />
                  Click to select a CSV file or drag and drop the CSV file here
                </>
              )}
            </label>
            <input
              id="csvFileInput"
              className="choose-file-button custom-input"
              type="file"
              accept=".csv"
              onChange={handleCSVUpload}
            />
          </div>

          <div className="onboard-form-actions">
            <button className="onboard-submit-btn" onClick={downloadTemplate}>
              Download Template
            </button>
            <button className="onboard-back-btn" onClick={handleClearCSV}>
              Clear Selection
            </button>
          </div>

          {csvImportState.file && (
            <>
              <div className="onboard-csv-status">
                Status: Processed {csvImportState.stats.processed} • 
                <span className="onboard-csv-valid"> Valid {csvImportState.stats.valid}</span> • 
                <span className="onboard-csv-invalid"> Invalid {csvImportState.stats.invalid}</span>
              </div>

              <div className="onboard-form-actions">
                <button 
                  className="onboard-submit-btn" 
                  onClick={handleImportCSV}
                  disabled={!csvImportState.validDevices.length || csvImportState.importing}
                >
                  {csvImportState.importing ? 'Importing...' : 'Import Valid Devices'}
                </button>
                {csvImportState.errors.length > 0 && (
                  <button className="onboard-submit-btn" onClick={downloadErrors}>
                    Download Errors
                  </button>
                )}
              </div>

              {csvImportState.validDevices.length > 0 && (
                <div className="onboard-preview-container">
                  <div className="onboard-preview-title">Valid Preview (first 5 rows)</div>
                  <div className="onboard-preview-table-wrap">
                    <table className="onboard-preview-table">
                      <thead>
                        <tr><th>Device ID</th><th>Make</th><th>City</th><th>Status</th></tr>
                      </thead>
                      <tbody>
                        {csvImportState.validDevices.slice(0, 5).map((device, idx) => (
                          <tr key={idx}>
                            <td>{device.id}</td>
                            <td>{device.meta.make}</td>
                            <td>{device.cityName}</td>
                            <td>{device.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {csvImportState.errors.length > 0 && (
                <div className="onboard-preview-container">
                  <div className="onboard-preview-title">Errors (first 5)</div>
                  <div className="onboard-preview-table-wrap">
                    <table className="onboard-preview-table">
                      <thead>
                        <tr><th>Line</th><th>Device ID</th><th>Issue</th></tr>
                      </thead>
                      <tbody>
                        {csvImportState.errors.slice(0, 5).map((error, idx) => (
                          <tr key={idx}>
                            <td>{error.row}</td>
                            <td>{error.device_id || '-'}</td>
                            <td>{error.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* BOTTOM: Onboarded Devices Table */}
      <div className="onboard-table-section">
        <h2 className="card-title">Onboarded Devices</h2>
        
        <div className="onboard-filter-section">
          <span>City Filter:</span>
          <Select
            styles={selectStyles}
            options={[{ value: 'All', label: 'All' }, ...apiCities.map(city => ({ value: city.city_name, label: city.city_name }))]}
            value={{ value: cityFilter, label: cityFilter }}
            onChange={(option) => setCityFilter(option?.value ?? 'All')}
            isClearable={false}
          />
        </div>

        <table className="onboard-data-table">
          <thead>
            <tr>
              <th>System ID</th><th>Make</th><th>Model</th><th>City Name</th>
              <th>State</th><th>Status</th><th>Max Throughput</th><th>Min Throughput</th>
              <th>Created On</th><th>Onboarded User</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredDevices.length === 0 ? (
              <tr>
                <td colSpan="11" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  {apiLoading.onboardedDevices ? 'Loading devices...' : 'No devices onboarded yet'}
                </td>
              </tr>
            ) : (
              filteredDevices.map((device) => {
                const isApiDevice = device.system_id !== undefined;
                const systemId = isApiDevice ? device.system_id : device.id;
                const make = isApiDevice ? device.make : device.meta?.make;
                const model = isApiDevice ? device.model : device.meta?.model;
                const cityName = isApiDevice ? device.city_name : device.cityName;
                const state = isApiDevice ? device.state : device.meta?.state;
                const status = device.status;
                const maxThroughput = isApiDevice ? device.throughput_max : device.meta?.thrMax;
                const minThroughput = isApiDevice ? device.throughput_min : device.meta?.thrMin;
                const createdOn = isApiDevice ? device.created_on : device.meta?.dateAdded;
                const onboardedUser = isApiDevice ? device.onboarded_user : null;

                return (
                  <tr key={systemId}>
                    <td>{systemId}</td>
                    <td>{make || 'N/A'}</td>
                    <td>{model || 'N/A'}</td>
                    <td>{cityName || 'N/A'}</td>
                    <td>{state || 'N/A'}</td>
                    <td>
                      <span className={`onboard-status-${status?.toLowerCase() || 'unknown'}`}>
                        {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'}
                      </span>
                    </td>
                    <td>{maxThroughput ? `${maxThroughput} Mbps` : 'N/A'}</td>
                    <td>{minThroughput ? `${minThroughput} Mbps` : 'N/A'}</td>
                    <td>{formatDate(createdOn)}</td>
                    <td>{onboardedUser || 'N/A'}</td>
                    <td>
                      <button
                        className="onboard-remove-btn"
                        onClick={() => handleDeboardDevice(systemId)}
                        disabled={deboardingId === systemId}
                      >
                        {deboardingId === systemId ? 'Deboarding...' : 'Deboard'}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default OnboardDeviceFleet;
